<template>
	<Toast position="bottom-right" group="br" />
	<Toast position="top-center" group="system-status">
		<template #message="slotProps">
			<div class="system-status-toast">
				<i v-if="slotProps.message.detail?.showLoader" class="mdi mdi-loading mdi-spin system-status-toast__spinner"></i>
				<div class="system-status-toast__content">
					<div v-if="slotProps.message.summary" class="system-status-toast__summary">{{ slotProps.message.summary }}</div>
					<div class="system-status-toast__detail">{{ slotProps.message.detail?.text ?? slotProps.message.detail }}</div>
				</div>
			</div>
		</template>
	</Toast>
	<ConfirmDialog></ConfirmDialog>
	<div v-if="loading" class="loadingSpinner" >
		<i class="mdi mdi-spin mdi-loading" style="fontSize: 8rem"></i>
	</div>
	<VerticalNavbar
		v-if="route.name && !isRouteWithoutLogin() && !loading && !route.fullPath.includes('system-administration') && !embeddedParam"
		:links="navbarLinks"
		:hideNavbar="!!apiKeyParam"
		@isUploadDialogShown="isUploadDialogShown"
		@onCollapseSidebar="opened = !opened"
		@userLoggedOut="opened = false"
	/>
	<div v-if="!loading" :class="{'view-open': opened, 'view': !isRouteWithoutLogin() && !route.fullPath.includes('system-administration') && !apiKeyParam && !embeddedParam, 'login-background-image': isRouteWithoutLogin() && !route.fullPath.includes('system-administration')}">
		<router-view :isUploadShown="isUploadShown"></router-view>
	</div>
</template>

<script lang="ts">

import VerticalNavbar from "@/components/VerticalNavbar.vue";
import ConfirmDialog from 'primevue/confirmdialog';
import {ClientManager} from "@/singletons/ClientManager";
import { useSqueezeStore } from '@/apps/squeeze/store';
import moment from "moment";
import {computed, defineComponent, onBeforeMount, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {changePalette} from "@/util/StylesHelper";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import useProductStore from "@/store/product-store";
import router from "@/router";
import {apiKeyUrlParam, documentTokenUrlParam, embeddedUrlParam, languageUrlParam, pageUrlParam, parameterUrlParam} from "@/util/EmbeddingParamHelper";
import {getMomentLocale} from "@/util/DateHelper";
import {Messages, SupportedLangs} from "@/lang";
import {usePrimeVue} from "primevue/config";

export default defineComponent({
	name: "App",
	components: {
		VerticalNavbar,
		ConfirmDialog,
	},
	emits: ["onCollapseSidebar"],
	setup() {
		/** Is the request for checking user-login currently loading? */
		const {t} = useI18n();
		const toast = useToast();

		const loading = ref(false);
		const opened = ref(false);
		const store = useSqueezeStore();
		const route = useRoute();
		const primevue = usePrimeVue();
		const { locale } = useI18n({ useScope: 'global' });

		// region Parameter

		const languageParam: string|null = languageUrlParam();
		if (languageParam !== null) {
			localStorage.setItem('language', languageParam);
		}

		// Apply the language here instead of relying on VerticalNavbar, which does the same on
		// mount but is not mounted at all in an embedded view — leaving it at the default locale.
		const activeLanguage = localStorage.getItem('language');
		if (activeLanguage && SupportedLangs.includes(activeLanguage)) {
			locale.value = activeLanguage;
			moment.locale(getMomentLocale(activeLanguage));
			// We need to use 'as any', because the regular way we can not write to the local object
			(primevue as any).config.locale = Messages[activeLanguage].PrimeVue;
		}

		const apiKeyParam = apiKeyUrlParam();
		const documentTokenParam = documentTokenUrlParam();
		const embeddedParam = embeddedUrlParam();
		ClientManager.initInstance(null, apiKeyParam, documentTokenParam);

		const pageParam: string|null = pageUrlParam();
		const parameter: string = parameterUrlParam();
		if (pageParam) {
			// The parameter is optional, since not every embeddable page needs one. Pages that do
			// (e.g. embeddableReports) show a message of their own when it is missing.
			router.push({ path: pageParam, query: parameter ? { parameter } : {}});
		}

		// endregion

		/** Api for Theme */
		const themeApi = ClientManager.getInstance().squeeze.theme;
		const storeProduct = useProductStore();

		const navbarLinks = computed(() => {
			const navBarLinks = [
				/*
				{
					label: "Login",
					to: {
						name: "Login",
						params: { lang: DEFAULT_LOCALE },
					},
				},
				 */
				/*{
					label: "Home",
					icon: "mdi mdi-home",
					isAdminRoute: false,
					to: {
						name: "Home",
					},
				},*/
				{
					label: t('Squeeze.General.Squeeze'),
					//icon: "mdi mdi-view-dashboard",
					icon: "mdi mdi-home",
					isAdminRoute: false,
					to: {
						name: "SqueezeDashboard",
					},
				},
				{
					label: t('Squeeze.General.Admin'),
					icon: "mdi mdi-cog",
					isAdminRoute: store.state.scopes.sqzAdminView,
					to: {
						name: "Administration",
					},
				},
				{
					label: t('Squeeze.Reports.Picker'),
					icon: "mdi mdi-chart-line",
					isAdminRoute: false,
					to: {
						name: "Reports",
					},
				},
			]

			if (storeProduct.state.freeze) {
				navBarLinks.push(
					{
						label: t('Freeze.Freeze'),
						//icon: "mdi mdi-view-dashboard",
						icon: "mdi mdi-archive",
						isAdminRoute: false,
						to: {
							name: "ArchiveSearchView",
						},
					})
			}

			return navBarLinks;
		});

		/** Is the upload dialog shown? */
		const isUploadShown = ref(false);

		/** Is the route a route that requires to be logged in? */
		const isRouteWithoutLogin = () => {
			if (route.meta && route.meta.requiresAuth === false) {
				return true;
			}

			return false;
		}

		/** Watch locale to reset locale storage if needed */
		watch(locale, () => {
			localStorage.setItem('language', locale.value);
			moment.locale(getMomentLocale(locale.value));
		})

		onMounted(() => {
			if(isRouteWithoutLogin() || !route.name) {
				loading.value = false;
				return;
			}

			loading.value = false;
		});

		/** Get main color of client */
		const getColor = () => {
			themeApi.getColor()
				.then(response => response.json().then ((color: string) => {
					if (color) {
						changePalette(color);
					}
				}))
				.catch((response: any) => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				}))
		}

		/** Triggered the upload dialog */
		const isUploadDialogShown = (isShown: boolean) => {
			isUploadShown.value = isShown;
		}

		onBeforeMount(() => {
			getColor();
			try {
				// get userSettings fontSize before loggedIn
				const localStorageFontSize = localStorage.getItem('fontSize');
				if (localStorageFontSize) {
					const fontSize = JSON.parse(localStorageFontSize);
					document.documentElement.style.setProperty("--global-font-size", fontSize + 'rem');
				}
			} catch(err) {
				return;
			}
		});

		return { navbarLinks, opened, loading, route, isUploadShown, apiKeyParam, embeddedParam,
			isRouteWithoutLogin, changePalette, getColor, isUploadDialogShown }
	},
});
</script>

<style lang="scss" scoped>

.top-level-router {
	padding: 0;
}

.system-status-toast {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.system-status-toast__spinner {
	font-size: 1.25rem;
}

.system-status-toast__content {
	display: flex;
	flex-direction: column;
}

.system-status-toast__summary {
	font-weight: 600;
}

.loadingSpinner {
	background: white;
	width: 100%;
	height: 100vh;
	position: fixed;
	text-align: center;
	vertical-align: middle;
	margin-top: 25%;
}

.view {
	//height: 100vh;
	transition: margin-left 0.5s;
	overflow: hidden;
	margin-left: 4.688rem;
}

.view-open {
	transition: margin-left 0.5s;
	overflow: hidden;
	margin-left: 9.375rem !important;
}

</style>

<style lang="scss">
	h1,h2 {
		&.view-title {
			margin: 0 0 2rem;
		}
	}

	h1.view-title {
		font-size: 4rem;
	}

	h2.view-title {
		font-size: 3.5rem;
	}

	html {
		font-size: var(--global-font-size) !important;
	}

	// Set Padding to null of setting content dialog
	.p-dialog .p-dialog-content.setting-dialog {
		padding: 0;
	}

</style>
